import React, { useMemo } from 'react';

import AllocationBar from '@/components/organisms/AllocationBar';
import InputField from '@/components/atoms/InputField';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { WorkActivity, WorkActivityAllocation, WorkActivityBankAllocation } from '@/services/types/work-activity.types';
import { formatWorkActivityTableDuration } from '@/utils/workActivityForm';

interface DurationAllocationItem {
  id: string;
  name: string;
  hours: number;
  minutes: number;
  color: string;
}

interface WorkActivityAllocationsDetailsProps {
  workActivity: WorkActivity;
}

const SECTIONS = [
  { type: 'DEPARTMENT_ACCOUNTING', label: 'Department Accounting Center' },
  { type: 'PRODUCTS_AND_SERVICES', label: 'Product and Services Accounting Center' },
  { type: 'PROJECTS', label: 'Projects Accounting Center' },
  { type: 'CONTRACTS', label: 'Contract Accounting Center' }
] as const;

const hslToHex = (h: number, s: number, l: number): string => {
  s /= 100;
  l /= 100;
  const k = (n: number) => (n + h / 30) % 12;
  const a = s * Math.min(l, 1 - l);
  const f = (n: number) => l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
  const r = Math.round(255 * f(0));
  const g = Math.round(255 * f(8));
  const b = Math.round(255 * f(4));
  return `#${[r, g, b].map((x) => x.toString(16).padStart(2, '0')).join('')}`;
};

const generateColorForIndex = (index: number): string => {
  const baseColors = ['#7663EB', '#48C80C'];
  if (index < baseColors.length) {
    return baseColors[index];
  }

  return hslToHex((index * 47) % 360, 70, 55);
};

const toDurationMinutes = (hours: number, minutes: number) => hours * 60 + minutes;

const mapAcAllocations = (items: WorkActivityAllocation[]): DurationAllocationItem[] =>
  items.map((item, index) => ({
    id: item.accounting_center_id,
    name: item.ac_name,
    hours: item.hours,
    minutes: item.minutes,
    color: generateColorForIndex(index)
  }));

const mapBankAllocations = (items: WorkActivityBankAllocation[]): DurationAllocationItem[] =>
  items.map((item, index) => ({
    id: item.bank_account_id,
    name: item.account_name,
    hours: item.hours,
    minutes: item.minutes,
    color: generateColorForIndex(index)
  }));

const WorkActivityAllocationsDetails: React.FC<WorkActivityAllocationsDetailsProps> = ({ workActivity }) => {
  const totalDurationMinutes = toDurationMinutes(workActivity.duration_hours, workActivity.duration_minutes);

  const groupedSections = useMemo(() => {
    return SECTIONS.map((section) => ({
      ...section,
      items: mapAcAllocations(workActivity.allocations.filter((allocation) => allocation.ac_type === section.type))
    })).filter((section) => section.items.length > 0);
  }, [workActivity.allocations]);

  const bankItems = useMemo(
    () => mapBankAllocations(workActivity.bank_allocations ?? []),
    [workActivity.bank_allocations]
  );

  const renderSection = (title: string, items: DurationAllocationItem[]) => (
    <div className="card space-y-4">
      <p className="font-medium text-sm md:text-base">{title}</p>
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <p className="text-sm font-medium text-secondary">Duration Allocation</p>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button type="button" className="text-primary font-bold text-xs">
                  ?
                </button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Duration split across selected items for this section.</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>

        <AllocationBar
          items={items.map((item) => ({
            value: toDurationMinutes(item.hours, item.minutes),
            label: '',
            color: item.color
          }))}
          maxAllowedTotal={totalDurationMinutes}
          height={22}
          borderRadius={12}
          isOverAllocated={false}
        />

        {items.map((item) => (
          <div key={item.id} className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-2 min-w-0">
              <span className="w-[9px] h-[9px] rounded-full shrink-0" style={{ background: item.color }} />
              <p className="text-xs md:text-sm font-medium leading-[100%] truncate">{item.name}</p>
            </div>
            <InputField
              name={`${item.id}-duration`}
              type="text"
              value={formatWorkActivityTableDuration(item.hours, item.minutes)}
              size="sm"
              className="w-[125px]"
              disabled
            />
          </div>
        ))}
      </div>
    </div>
  );

  if (groupedSections.length === 0 && bankItems.length === 0) {
    return <p className="font-medium text-base text-secondary">No allocations found</p>;
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {bankItems.length > 0 && renderSection('Bank Account', bankItems)}
      {groupedSections.map((section) => (
        <React.Fragment key={section.type}>{renderSection(section.label, section.items)}</React.Fragment>
      ))}
    </div>
  );
};

export default WorkActivityAllocationsDetails;
